/**
 * SOCIALIZA - Editorial Types Registry
 *
 * Este arquivo é a fonte única de verdade para todos os tipos editoriais.
 * Qualquer novo tipo deve ser adicionado aqui.
 *
 * Estrutura de cada tipo:
 * - id: Identificador único (usado no banco de dados)
 * - name: Nome de exibição
 * - icon: Componente SVG ou emoji
 * - color: Cor tema do tipo
 * - category: Categoria do tipo
 * - dataField: Nome do campo no modelo Post (ex: "tiktokData")
 * - requiresVideo: Se requer upload de vídeo
 * - requiresAudio: Se requer upload de áudio
 * - requiresImage: Se requer upload de imagem
 * - feedLayout: Tipo de layout do feed ("vertical-scroll", "grid", "list", "player", "fullscreen")
 * - implemented: Se já está implementado no sistema
 */

// ============================================
// CATEGORIA: Social & Media
// ============================================
export const socialMediaTypes = [
  {
    id: "post_feed",
    name: "Post",
    emoji: "📝",
    color: "#1877f2",
    category: "social",
    dataField: null,
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Post tradicional do feed",
  },
  {
    id: "threads",
    name: "Conversas",
    emoji: "💬",
    color: "#000000",
    category: "social",
    dataField: "threadsData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Conversas em thread e discussões",
  },
  {
    id: "live_audio",
    name: "Sala de Áudio",
    emoji: "🎙️",
    color: "#1DB954",
    category: "social",
    dataField: "liveAudioData",
    requiresVideo: false,
    requiresAudio: true,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Salas de áudio ao vivo estilo Clubhouse/Spaces",
  },
  {
    id: "music_player",
    name: "Músicas",
    emoji: "🎶",
    color: "#1DB954",
    category: "social",
    dataField: "musicData",
    requiresVideo: false,
    requiresAudio: true,
    requiresImage: false,
    feedLayout: "player",
    implemented: true,
    description: "Compartilhamento de músicas e álbuns",
  },
  {
    id: "audio",
    name: "Áudio",
    emoji: "🔊",
    color: "#FF6B6B",
    category: "social",
    dataField: "audioData",
    requiresVideo: false,
    requiresAudio: true,
    requiresImage: false,
    feedLayout: "player",
    implemented: true,
    description: "Posts de áudio/voice notes",
  },
  {
    id: "sticker",
    name: "Sticker",
    emoji: "🎨",
    color: "#FF9500",
    category: "social",
    dataField: "stickerData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Stickers e figurinhas",
  },
  {
    id: "gif",
    name: "GIF",
    emoji: "🎬",
    color: "#00F5D4",
    category: "social",
    dataField: "gifData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "GIFs animados",
  },
];

// ============================================
// CATEGORIA: Video & Streaming
// ============================================
export const videoStreamingTypes = [
  {
    id: "short_video",
    name: "Vídeos Curtos",
    emoji: "📱",
    color: "#000000",
    category: "video",
    dataField: "shortVideoData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "vertical-scroll",
    implemented: true,
    description: "Vídeos curtos verticais",
  },
  {
    id: "vertical_clip",
    name: "Clipes Verticais",
    emoji: "🎞️",
    color: "#c13584",
    category: "video",
    dataField: "shortVideoData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "vertical-scroll",
    implemented: true,
    description: "Vídeos curtos em formato vertical",
  },
  {
    id: "quick_video",
    name: "Vídeos Rápidos",
    emoji: "⚡",
    color: "#ff0000",
    category: "video",
    dataField: "shortVideoData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "vertical-scroll",
    implemented: true,
    description: "Vídeos rápidos e dinâmicos",
  },
  {
    id: "long_video",
    name: "Vídeos Longos",
    emoji: "📹",
    color: "#ff0000",
    category: "video",
    dataField: "longVideoData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Vídeos longos e conteúdo completo",
  },
  {
    id: "streaming_media",
    name: "Streaming",
    emoji: "🎭",
    color: "#e50914",
    category: "video",
    dataField: "streamingData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Filmes e séries em streaming",
  },
  {
    id: "video_bank",
    name: "Banco de Vídeos",
    emoji: "🎥",
    color: "#6366F1",
    category: "video",
    dataField: "videoBankData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Biblioteca de vídeos organizados",
  },
  {
    id: "live_streaming",
    name: "Live",
    emoji: "🔴",
    color: "#FF0000",
    category: "video",
    dataField: "liveStreamData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "fullscreen",
    implemented: true,
    description: "Transmissões ao vivo",
  },
  {
    id: "movie_portal",
    name: "Cinema",
    emoji: "🍿",
    color: "#8B5CF6",
    category: "video",
    dataField: "moviePortalData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Portal de filmes e séries",
  },
];

// ============================================
// CATEGORIA: Gaming
// ============================================
export const gamingTypes = [
  {
    id: "gaming",
    name: "Gaming",
    emoji: "🎮",
    color: "#9146ff",
    category: "gaming",
    dataField: "gamingData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Clips e streams de jogos",
  },
  {
    id: "esports",
    name: "E-Sports",
    emoji: "🏆",
    color: "#00D4FF",
    category: "gaming",
    dataField: "esportsData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Competições e torneios",
  },
  {
    id: "game_review",
    name: "Game Review",
    emoji: "⭐",
    color: "#FFD700",
    category: "gaming",
    dataField: "gameReviewData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Reviews e análises de jogos",
  },
];

// ============================================
// CATEGORIA: News & Content
// ============================================
export const newsContentTypes = [
  {
    id: "news_article",
    name: "Notícias",
    emoji: "📰",
    color: "#cc0000",
    category: "news",
    dataField: "newsData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Artigos e reportagens de notícias",
  },
  {
    id: "blog",
    name: "Blog",
    emoji: "✍️",
    color: "#0077b5",
    category: "news",
    dataField: "blogData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Posts de blog",
  },
  {
    id: "podcast",
    name: "Podcast",
    emoji: "🎧",
    color: "#8b5cf6",
    category: "news",
    dataField: "podcastData",
    requiresVideo: false,
    requiresAudio: true,
    requiresImage: false,
    feedLayout: "player",
    implemented: true,
    description: "Episódios de podcast",
  },
  {
    id: "portfolio",
    name: "Portfólio",
    emoji: "💼",
    color: "#2DD4BF",
    category: "news",
    dataField: "portfolioData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Trabalhos e projetos",
  },
  {
    id: "library",
    name: "Biblioteca",
    emoji: "📚",
    color: "#8B4513",
    category: "news",
    dataField: "libraryData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Coleção de livros e recursos",
  },
  {
    id: "ebook",
    name: "E-Book",
    emoji: "📖",
    color: "#059669",
    category: "news",
    dataField: "ebookData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Livros digitais",
  },
  {
    id: "pdf",
    name: "PDF/Documento",
    emoji: "📄",
    color: "#DC2626",
    category: "news",
    dataField: "pdfData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Documentos PDF",
  },
];

// ============================================
// CATEGORIA: Educação
// ============================================
export const educationTypes = [
  {
    id: "course",
    name: "Cursos",
    emoji: "🎓",
    color: "#A855F7",
    category: "education",
    dataField: "courseData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Cursos online estilo Udemy",
  },
  {
    id: "estudent",
    name: "E-Student",
    emoji: "👨‍🎓",
    color: "#3B82F6",
    category: "education",
    dataField: "estudentData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Plataforma estudantil",
  },
  {
    id: "school",
    name: "Escola",
    emoji: "🏫",
    color: "#10B981",
    category: "education",
    dataField: "schoolData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Gestão escolar",
  },
  {
    id: "quiz",
    name: "Quiz",
    emoji: "❓",
    color: "#F59E0B",
    category: "education",
    dataField: "quizData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Quizzes e questionários",
  },
  {
    id: "voting",
    name: "Votação",
    emoji: "🗳️",
    color: "#6366F1",
    category: "education",
    dataField: "votingData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Sistema de votação blockchain",
  },
  {
    id: "tutorial",
    name: "Tutorial",
    emoji: "📋",
    color: "#14B8A6",
    category: "education",
    dataField: "tutorialData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Tutoriais passo a passo",
  },
];

// ============================================
// CATEGORIA: Comércio & Serviços
// ============================================
export const commerceTypes = [
  {
    id: "eshop",
    name: "Loja",
    emoji: "🛒",
    color: "#10b981",
    category: "commerce",
    dataField: "eshopData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Produtos à venda",
  },
  {
    id: "marketplace",
    name: "Marketplace",
    emoji: "🛍️",
    color: "#ff6600",
    category: "commerce",
    dataField: "marketplaceData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Marketplace global de produtos",
  },
  {
    id: "restaurant",
    name: "Restaurante",
    emoji: "🍽️",
    color: "#EF4444",
    category: "commerce",
    dataField: "restaurantData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Cardápio e reservas",
  },
  {
    id: "food_delivery",
    name: "Delivery",
    emoji: "🛵",
    color: "#F97316",
    category: "commerce",
    dataField: "foodDeliveryData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Pedidos de comida",
  },
  {
    id: "booking",
    name: "Reservas",
    emoji: "🏨",
    color: "#003580",
    category: "commerce",
    dataField: "bookingData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Reservas de hotéis",
  },
  {
    id: "lodging",
    name: "Hospedagem",
    emoji: "🏡",
    color: "#FF5A5F",
    category: "commerce",
    dataField: "lodgingData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Aluguel de imóveis para temporada",
  },
  {
    id: "rental",
    name: "Aluguel",
    emoji: "🔑",
    color: "#7C3AED",
    category: "commerce",
    dataField: "rentalData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Aluguel de itens diversos",
  },
  {
    id: "mobility",
    name: "Mobilidade",
    emoji: "🚕",
    color: "#000000",
    category: "commerce",
    dataField: "mobilityData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Serviços de transporte e mobilidade",
  },
  {
    id: "job_service",
    name: "Serviços",
    emoji: "🔧",
    color: "#0EA5E9",
    category: "commerce",
    dataField: "jobServiceData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Ofertas de serviços",
  },
  {
    id: "event",
    name: "Eventos",
    emoji: "🎪",
    color: "#EC4899",
    category: "commerce",
    dataField: "eventData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Eventos e ingressos",
  },
  {
    id: "classified",
    name: "Classificados",
    emoji: "📋",
    color: "#84CC16",
    category: "commerce",
    dataField: "classifiedData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "list",
    implemented: true,
    description: "Anúncios classificados",
  },
];

// ============================================
// CATEGORIA: Saúde & Social
// ============================================
export const healthSocialTypes = [
  {
    id: "medical",
    name: "Saúde",
    emoji: "🏥",
    color: "#06B6D4",
    category: "health",
    dataField: "medicalData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Gestão médica e consultas",
  },
  {
    id: "ong",
    name: "ONG",
    emoji: "💚",
    color: "#22C55E",
    category: "health",
    dataField: "ongData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Organizações não governamentais",
  },
  {
    id: "crowdfunding",
    name: "Vaquinha",
    emoji: "💰",
    color: "#EAB308",
    category: "health",
    dataField: "crowdfundingData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Campanhas de arrecadação",
  },
  {
    id: "community",
    name: "Comunidade",
    emoji: "👥",
    color: "#8B5CF6",
    category: "health",
    dataField: "communityData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Grupos e comunidades",
  },
  {
    id: "petition",
    name: "Petição",
    emoji: "✊",
    color: "#DC2626",
    category: "health",
    dataField: "petitionData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "list",
    implemented: true,
    description: "Abaixo-assinados",
  },
];

// ============================================
// CATEGORIA: Wellness & Lifestyle
// ============================================
export const wellnessTypes = [
  {
    id: "travel",
    name: "Viagem",
    emoji: "✈️",
    color: "#0891B2",
    category: "wellness",
    dataField: "travelData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Destinos e dicas de viagem",
  },
  {
    id: "car",
    name: "Automóveis",
    emoji: "🚙",
    color: "#475569",
    category: "wellness",
    dataField: "carData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Carros e veículos",
  },
  {
    id: "yoga",
    name: "Yoga",
    emoji: "🧘",
    color: "#A78BFA",
    category: "wellness",
    dataField: "yogaData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Aulas e práticas de yoga",
  },
  {
    id: "exercise",
    name: "Exercícios",
    emoji: "💪",
    color: "#F43F5E",
    category: "wellness",
    dataField: "exerciseData",
    requiresVideo: true,
    requiresAudio: false,
    requiresImage: false,
    feedLayout: "grid",
    implemented: true,
    description: "Treinos e exercícios",
  },
  {
    id: "recipe",
    name: "Receitas",
    emoji: "👨‍🍳",
    color: "#FB923C",
    category: "wellness",
    dataField: "recipeData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Receitas culinárias",
  },
  {
    id: "fashion",
    name: "Moda",
    emoji: "👗",
    color: "#EC4899",
    category: "wellness",
    dataField: "fashionData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Looks e tendências",
  },
  {
    id: "beauty",
    name: "Beleza",
    emoji: "💄",
    color: "#F472B6",
    category: "wellness",
    dataField: "beautyData",
    requiresVideo: false,
    requiresAudio: false,
    requiresImage: true,
    feedLayout: "grid",
    implemented: true,
    description: "Dicas de beleza e cuidados",
  },
];

// ============================================
// COMBINAÇÃO DE TODOS OS TIPOS
// ============================================
export const allEditorialTypes = [
  ...socialMediaTypes,
  ...videoStreamingTypes,
  ...gamingTypes,
  ...newsContentTypes,
  ...educationTypes,
  ...commerceTypes,
  ...healthSocialTypes,
  ...wellnessTypes,
];

// ============================================
// CATEGORIAS
// ============================================
export const editorialCategories = [
  {
    id: "social",
    name: "Social & Media",
    emoji: "💬",
    color: "#1877f2",
    types: socialMediaTypes,
  },
  {
    id: "video",
    name: "Vídeo & Streaming",
    emoji: "📹",
    color: "#ff0000",
    types: videoStreamingTypes,
  },
  {
    id: "gaming",
    name: "Gaming",
    emoji: "🎮",
    color: "#9146ff",
    types: gamingTypes,
  },
  {
    id: "news",
    name: "Notícias & Conteúdo",
    emoji: "📰",
    color: "#cc0000",
    types: newsContentTypes,
  },
  {
    id: "education",
    name: "Educação",
    emoji: "🎓",
    color: "#A855F7",
    types: educationTypes,
  },
  {
    id: "commerce",
    name: "Comércio & Serviços",
    emoji: "🛒",
    color: "#10b981",
    types: commerceTypes,
  },
  {
    id: "health",
    name: "Saúde & Social",
    emoji: "💚",
    color: "#22C55E",
    types: healthSocialTypes,
  },
  {
    id: "wellness",
    name: "Wellness & Lifestyle",
    emoji: "✨",
    color: "#F43F5E",
    types: wellnessTypes,
  },
];

// ============================================
// FUNÇÕES UTILITÁRIAS
// ============================================

/**
 * Obtém um tipo editorial pelo ID
 */
export const getEditorialType = (id) => {
  return allEditorialTypes.find((type) => type.id === id);
};

/**
 * Obtém todos os tipos implementados
 */
export const getImplementedTypes = () => {
  return allEditorialTypes.filter((type) => type.implemented);
};

/**
 * Obtém todos os tipos não implementados
 */
export const getPendingTypes = () => {
  return allEditorialTypes.filter((type) => !type.implemented);
};

/**
 * Obtém tipos por categoria
 */
export const getTypesByCategory = (categoryId) => {
  const category = editorialCategories.find((cat) => cat.id === categoryId);
  return category ? category.types : [];
};

/**
 * Obtém o nome do campo de dados para um tipo
 */
export const getDataFieldName = (typeId) => {
  const type = getEditorialType(typeId);
  return type?.dataField || null;
};

/**
 * Verifica se um tipo requer vídeo
 */
export const typeRequiresVideo = (typeId) => {
  const type = getEditorialType(typeId);
  return type?.requiresVideo || false;
};

/**
 * Verifica se um tipo requer áudio
 */
export const typeRequiresAudio = (typeId) => {
  const type = getEditorialType(typeId);
  return type?.requiresAudio || false;
};

/**
 * Verifica se um tipo requer imagem
 */
export const typeRequiresImage = (typeId) => {
  const type = getEditorialType(typeId);
  return type?.requiresImage || false;
};

/**
 * Obtém o layout do feed para um tipo
 */
export const getFeedLayout = (typeId) => {
  const type = getEditorialType(typeId);
  return type?.feedLayout || "list";
};

/**
 * Obtém estatísticas do registry
 */
export const getRegistryStats = () => {
  const implemented = getImplementedTypes();
  const pending = getPendingTypes();

  return {
    total: allEditorialTypes.length,
    implemented: implemented.length,
    pending: pending.length,
    byCategory: editorialCategories.map((cat) => ({
      category: cat.name,
      total: cat.types.length,
      implemented: cat.types.filter((t) => t.implemented).length,
    })),
  };
};

/**
 * Mapeamento de IDs para campos de dados (compatibilidade com código existente)
 */
export const dataFieldMap = allEditorialTypes.reduce((acc, type) => {
  if (type.dataField) {
    acc[type.id] = type.dataField;
  }
  return acc;
}, {});

/**
 * Lista de todos os valores de editorialType para enum do MongoDB
 */
export const editorialTypeEnum = allEditorialTypes.map((type) => type.id);

const editorialRegistry = {
  allEditorialTypes,
  editorialCategories,
  socialMediaTypes,
  videoStreamingTypes,
  gamingTypes,
  newsContentTypes,
  educationTypes,
  commerceTypes,
  healthSocialTypes,
  wellnessTypes,
  getEditorialType,
  getImplementedTypes,
  getPendingTypes,
  getTypesByCategory,
  getDataFieldName,
  typeRequiresVideo,
  typeRequiresAudio,
  typeRequiresImage,
  getFeedLayout,
  getRegistryStats,
  dataFieldMap,
  editorialTypeEnum,
};

export default editorialRegistry;
